export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    credentials: "same-origin",
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const message =
      typeof payload === "object" && payload !== null && "error" in payload && typeof payload.error === "string"
        ? payload.error
        : "That did not work. Try again.";
    throw new ApiError(response.status, message);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

export type SessionInfo = { role: "guide"; guideId: string } | { role: "parent"; familyId: string };

export interface GuideInfo {
  id: string;
  name: string;
  email: string;
  publicKey: string | null;
  schedulingUrl: string | null;
}

export interface Me {
  session: SessionInfo;
  guide?: GuideInfo | null;
}

export interface SessionStatus {
  session: SessionInfo | null;
  devLogin: boolean;
}

export function sessionStatus(): Promise<SessionStatus> {
  return api.get<SessionStatus>("/session");
}

/** Returns null when nobody is signed in, so pages can send the visitor to the front door. */
export async function me(): Promise<Me | null> {
  const { session } = await sessionStatus();
  return session ? api.get<Me>("/me") : null;
}
